import React, { useState, useRef, useImperativeHandle } from "react";
import { Popconfirm, Tooltip, theme } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusCircleFilled,
} from "@ant-design/icons";
import TSTable from "../../../common/TSTable";
import ColorButton from "../../../common/ColorButton";
import { DELETE } from "../../../frameworks/HttpClient";
import { URL_SETTING } from "../../../constants/urls";
import FactoryBankAccountModal from "./FactoryBankAccountModal";
import { dateTimeToHumanString } from "../../../frameworks/Util";
import ErrorMessages from "../../../common/ErrorMessages";
import CustomDeleteConfirmButton from "../../../common/CustomDeleteConfirmButton";


export const FactoryBankAccountList = React.forwardRef((props, ref) => {
  const { isDone } = props;
  const [openAddFactoryBankAccountModal, setOpenAddFactoryBankAccountModal] = useState(false);
  const [target, setTarget] = useState(null);
  const tableRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const {
    token: { colorWarning, colorError, colorSuccess },
  } = theme.useToken();

  useImperativeHandle(ref, () => ({
    fetchTable: refreshData,
  }));

  const refreshData = async () => {
    if (tableRef.current) {
      setErrorMessage(null);
      try {
        await tableRef.current.fetchWithoutResetPage();
      } catch (error) {
        setErrorMessage(error.errorMessage);
      }
    }
  };

  const handleEdit = (record) => {
    if (!record?.id) return;
    setTarget(record);
    setOpenAddFactoryBankAccountModal(true);
  };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      await DELETE(`${URL_SETTING.BANK_ACCOUNT}${id}/`);
      refreshData();
    } catch (error) {}
  };

  const columns = [
    {
      title: "Action",
      dataIndex: "",
      width: 200,
      key: "action",
      align: "center",
      render: (_, record) => (
          <div onClick={(e) => e.stopPropagation()}>
            {record.is_active === true && (
            <CustomDeleteConfirmButton
              title={`ยืนยันลบหรือไม่`}
              content={"ยืนยันการลบรายการนี้หรือไม่ ?"}
              onOk={ async() => {
                await handleDelete(record.id)
                refreshData();
              }}
              onConfirm={() => refreshData()}
            />
            )}
            <Tooltip trigger="hover" title="แก้ไขข้อมูล">
              <ColorButton
                type="primary"
                override={colorWarning}
                style={{ margin : 5}}
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          </div>
      ),
    },    
    {
      title: "ชื่อบัญชี",
      dataIndex: "account_name",
      key: "account_name",
      ellipsis: true,
      align: "center",
      searcher: true,
    },
    {
      title: "ชื่อธนาคาร",
      dataIndex: "bank_name",
      key: "bank_name",
      align: "center",
    },
    {
      title: "เลขที่บัญชี",
      dataIndex: "account_no",
      key: "account_no",
      align: "center",
    },
    {
      title: "สถานะ",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      render: (isActive) => (
        <span style={{ color: isActive ? colorSuccess : colorError }}>
          {isActive ? "ใช้งาน" : "ไม่ใช้งาน"}
        </span>
      ),
    },
    
    {
      title: "วันที่สร้าง",
      dataIndex: "created",
      key: "created",
      align: "center",
      render: (value) => dateTimeToHumanString(value),
    },
  ];

  return (
    <div>
      <ErrorMessages message={errorMessage}/>
      <ColorButton
        override={colorSuccess}
        type="primary"
        style={{ marginBottom: 10, float: "right" }}
        icon={<PlusCircleFilled />}
        onClick={() => setOpenAddFactoryBankAccountModal(true)}
      >
        เพิ่มบัญชีธนาคาร
      </ColorButton>
      <TSTable
        columns={columns}
        size="small"
        url={`${URL_SETTING.BANK_ACCOUNT}`}
        ref={tableRef}
      />
      <FactoryBankAccountModal
        open={openAddFactoryBankAccountModal}
        onClose={() => {
          setOpenAddFactoryBankAccountModal(false);
          setTarget(null);
        }}
        onUpdate={() => { 
          setOpenAddFactoryBankAccountModal(false);
          setTarget(null);
          refreshData();
        }}
        target={target}
      />
    </div>
  );
});

export default FactoryBankAccountList;
